import type React from 'react'
import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import { type OccupationType } from 'components'

export interface PersonBackgroundDataType {
  code: string
  description: string
  key: keyof PersonBackgroundInfoResponse
  icon: React.ElementType
  selectedIcon: React.ElementType
}

export interface BaseBackgroundInfo {
  id: number
  personId: number
  displayOrder: number
  updatedBy: string
  lastUpdated: string
}
export interface KnowWhoBio extends BaseBackgroundInfo {
  bioType: string
  bioText: string
  htmlReady: boolean
  createdBy: string
  lastCreated: string
}

export interface NarrativeBio extends BaseBackgroundInfo {
  bioText: string
  bioSource: string
  bioDate: string | null
}

export interface Award extends BaseBackgroundInfo {
  typeId: number
  organization: string
  awardTitle: string
  awardYear: string | null
  details: string | null
}

export interface Director extends BaseBackgroundInfo {
  typeId: number
  company: string
  title: string
  startYear: string | null
  endYear: string | null
  details: string | null
}

export interface Education extends BaseBackgroundInfo {
  degreeId: number
  majors: string | null
  minors: string | null
  schoolName: string
  startDate: string | null
  endDate: string | null
  details: string | null
}

export interface HobbyInterest extends BaseBackgroundInfo {
  interest: string
}

export interface MilitaryService extends BaseBackgroundInfo {
  branch: string
  startRank: string | null
  highestRank: string | null
  medals: string | null
  details: string | null
}

export interface NotableFact extends BaseBackgroundInfo {
  factText: string
  factSource: string
  factDate: string
}

export interface PoliticalExperience extends BaseBackgroundInfo {
  organization: string | null
  title: string | null
  experienceDesc: string | null
  startDate: string | null
  endDate: string | null
}

export interface OrgMembership extends BaseBackgroundInfo {
  typeId: number
  organization: string
  title: string
  startYear: string | null
  endYear: string | null
  details: string | null
}

export interface PreviousOccupation extends BaseBackgroundInfo {
  occCode: OccupationType | null
  occupation: string
  company: string | null
  startDate: string | null
  endDate: string | null
  details: string | null
}

export interface PublicationCredit extends BaseBackgroundInfo {
  typeId: number
  pubByIn: string | null
  pubTitle: string
  pubYear: string | null
  details: string | null
}

export interface PersonBackgroundInfoResponse {
  background_infos: KnowWhoBio[]
  narrative_bios: NarrativeBio[]
  awards: Award[]
  directors: Director[]
  education: Education[]
  interests: HobbyInterest[]
  military_service: MilitaryService[]
  facts: NotableFact[]
  memberships: OrgMembership[]
  experience: PoliticalExperience[]
  occupations: PreviousOccupation[]
  publications: PublicationCredit[]

}

export async function getPersonBackgroundInfo (personId: number): Promise<HttpResponse<PersonBackgroundInfoResponse>> {
  return await handleHttpRequest<PersonBackgroundInfoResponse>(http.get(`/api/v1/us/person/${personId}/backgroundinfo/`))
}

export async function updateBackgroundInfo<T extends BaseBackgroundInfo> (
  item: T,
  dataTypeKey: keyof PersonBackgroundInfoResponse
): Promise<HttpResponse<T>> {
  return await handleHttpRequest(http.put(`/api/v1/us/person/${item.personId}/backgroundinfo/${item.id}/?type=${dataTypeKey}`, item))
}

export async function createBackgroundInfo<T extends BaseBackgroundInfo> (
  item: T,
  dataTypeKey: keyof PersonBackgroundInfoResponse
): Promise<HttpResponse<T>> {
  return await handleHttpRequest(http.post(`/api/v1/us/person/${item.personId}/backgroundinfo/?type=${dataTypeKey}`, item))
}

export async function deleteBackgroundInfo<T extends BaseBackgroundInfo> (
  item: T,
  dataTypeKey: keyof PersonBackgroundInfoResponse
): Promise<HttpResponse<void>> {
  return await handleHttpRequest(http.delete(`/api/v1/us/person/${item.personId}/backgroundinfo/${item.id}/?type=${dataTypeKey}`))
}

export async function updateAward (award: Award): Promise<HttpResponse<Award>> {
  return await updateBackgroundInfo(award, 'awards')
}

export async function updateDirector (director: Director): Promise<HttpResponse<Director>> {
  return await updateBackgroundInfo(director, 'directors')
}

export async function updateEducation (education: Education): Promise<HttpResponse<Education>> {
  return await updateBackgroundInfo(education, 'education')
}

export async function updateHobbyInterest (interest: HobbyInterest): Promise<HttpResponse<HobbyInterest>> {
  return await updateBackgroundInfo(interest, 'interests')
}

export async function updateMilitaryService (service: MilitaryService): Promise<HttpResponse<MilitaryService>> {
  return await updateBackgroundInfo(service, 'military_service')
}

export async function updateNotableFact (fact: NotableFact): Promise<HttpResponse<NotableFact>> {
  return await updateBackgroundInfo(fact, 'facts')
}

export async function updateOrgMembership (membership: OrgMembership): Promise<HttpResponse<OrgMembership>> {
  return await updateBackgroundInfo(membership, 'memberships')
}

export async function updatePoliticalExperience (experience: PoliticalExperience): Promise<HttpResponse<PoliticalExperience>> {
  return await updateBackgroundInfo(experience, 'experience')
}

export async function updatePreviousOccupation (occupation: PreviousOccupation): Promise<HttpResponse<PreviousOccupation>> {
  return await updateBackgroundInfo(occupation, 'occupations')
}

export async function updatePublicationCredit (publication: PublicationCredit): Promise<HttpResponse<PublicationCredit>> {
  return await updateBackgroundInfo(publication, 'publications')
}

export async function updateBackgroundInfoDisplayOrder<T extends BaseBackgroundInfo> (
  personId: number,
  items: T[],
  dataTypeKey: keyof PersonBackgroundInfoResponse
): Promise<HttpResponse<T[]>> {
  const idAndDisplayOrderMap = items.reduce<Record<number, number>>((acc, item, index) => {
    acc[item.id] = index + 1
    return acc
  }, {})

  return await handleHttpRequest(
    http.patch(`/api/v1/us/person/${personId}/backgroundinfo/?type=${dataTypeKey}`, {
      idAndDisplayOrderMap
    })
  )
}

export async function updateKnowWhoBio (bio: KnowWhoBio): Promise<HttpResponse<KnowWhoBio>> {
  return await updateBackgroundInfo(bio, 'background_infos')
}

export async function updateNarrativeBio (bio: NarrativeBio): Promise<HttpResponse<NarrativeBio>> {
  return await updateBackgroundInfo(bio, 'narrative_bios')
}
